import React, { useId, useMemo, useRef, useState } from 'react';
import type { ChatSource } from '../../lookupApi';
import {
    FindingsEmptyState, FindingsListHeader, FindingsToggleButton, SourceGroup, regulatorySource,
    type Finding, type RefLeaf,
} from '../workspace/findingsTree';
import { eurLexTextDestination } from './conceptLinks';
import { libraryTarget } from './libraryLinks';
import { useLibraryPanel } from './LibraryPanel';

/**
 * Chat's findings (docs/specs/lookup.md, "Findings"): the sources a chat's
 * answers cited, saved as Findings in the chat's workspace, shown with the
 * Research workspace's own findings button and list (workspace/findingsTree),
 * so they read as findings at once. A row opens its source in the Library
 * panel at the cited passage; with no Library, or a source the Library cannot
 * show (a PDF page), EUR-Lex or the PDF in a new tab.
 */

/** A source's link as an answer would carry it: with its passage as a text
 *  fragment, so the Library (or EUR-Lex) marks it. */
export function sourceHref(source: ChatSource): string {
    if (!source.passage) return source.url;
    return `${source.url}${source.url.includes('#') ? '' : '#'}:~:text=${encodeURIComponent(source.passage)}`;
}

/** The Research findings button, in Chat's header. */
export function ChatFindingsButton({ count, open, onToggle }: { count: number; open: boolean; onToggle: () => void }) {
    return <FindingsToggleButton findingsCount={count} onToggle={onToggle} position="relative" label={`Findings ${count}`} expanded={open} />;
}

/** The Research findings list for the chat's findings. Rendered inside the
 *  chat's LibraryPanelHost, so a row opens the Library beside it. */
export function ChatFindingsPanel({ sources, onClose }: { sources: ChatSource[]; onClose: () => void }) {
    const library = useLibraryPanel();
    const [expanded, setExpanded] = useState(true);
    const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
    const [selected, setSelected] = useState<string | null>(null);
    const byId = useMemo(() => new Map(sources.map(source => [source.finding_id, source])), [sources]);
    const tree = useMemo(() => regulatorySource(sources.map(source => source.finding as Finding)), [sources]);
    const headingId = useId();
    const list = useRef<HTMLDivElement>(null);

    const openLeaf = (leaf: RefLeaf) => {
        const source = leaf.finding && byId.get(leaf.finding.finding_id);
        if (!source) return;
        setSelected(leaf.id);
        const href = sourceHref(source);
        const destination = eurLexTextDestination(href);
        const target = library ? libraryTarget(href, library.origin) : null;
        // The row's own button: focus returns to it when the Library closes.
        const id = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(source.finding_id) : source.finding_id;
        const row = list.current?.querySelector<HTMLElement>(`[data-finding-id="${id}"] button`) ?? null;
        if (library && target && row) library.open({ target, eurlex: destination, label: source.label }, row);
        else window.open(destination, '_blank', 'noopener,noreferrer');
    };

    return (
        <section aria-labelledby={headingId} className="h-full bg-surface border-l border-border-default shadow-sm flex flex-col overflow-hidden">
            <FindingsListHeader title="Findings" count={tree?.count ?? 0} onClose={onClose} headingId={headingId} />
            <div ref={list} className="flex-1 overflow-y-auto p-1.5">
                {tree ? (
                    <SourceGroup
                        source={tree}
                        expanded={expanded}
                        onToggle={() => setExpanded(open => !open)}
                        collapsedSections={collapsed}
                        onSectionToggle={id => setCollapsed(prev => {
                            const next = new Set(prev);
                            if (next.has(id)) next.delete(id); else next.add(id);
                            return next;
                        })}
                        selectedLeafId={selected}
                        selectedOnlineId={null}
                        onLeafClick={openLeaf}
                        onLeafDelete={() => undefined}
                        deletingUploadId={null}
                    />
                ) : (
                    <FindingsEmptyState text="No findings yet" />
                )}
            </div>
        </section>
    );
}
